import { differenceInCalendarDays, parseISO, startOfDay } from 'date-fns';

import { buildPetFoodStatus, type SharedPetFoodRowInput } from './pet-food';

export type PetAttentionPet = {
  id: string;
  name: string | null | undefined;
  petType?: string | null | undefined;
};

export type PetAttentionReminder = {
  id: string;
  title: string | null | undefined;
  dueDate: string | null | undefined;
  completedAt?: string | null | undefined;
  subjectType?: string | null | undefined;
  subjectId?: string | null | undefined;
};

export type PetAttentionFoodProfile = SharedPetFoodRowInput & {
  id?: string | null | undefined;
  pet_id: string;
};

export type PetAttentionSurfaceItem = {
  id: string;
  emoji: string;
  text: string;
  path: string;
  label: string;
  score: number;
  kind: 'pet-care' | 'pet-food' | 'pet-food-setup';
  daysUntil: number | null;
};

function normalisePetName(value: string | null | undefined) {
  const trimmed = String(value || '').trim();
  return trimmed.length ? trimmed : 'your pet';
}

function petEmoji(petType: string | null | undefined) {
  const normalised = String(petType || '').trim().toLowerCase();
  if (normalised.includes('dog')) return '🐶';
  if (normalised.includes('cat')) return '🐱';
  if (normalised.includes('rabbit') || normalised.includes('bunny')) return '🐰';
  if (normalised.includes('bird')) return '🐦';
  if (normalised.includes('fish')) return '🐠';
  if (normalised.includes('hamster') || normalised.includes('guinea') || normalised.includes('rodent')) return '🐹';
  if (normalised.includes('horse')) return '🐴';
  if (normalised.includes('reptile') || normalised.includes('lizard') || normalised.includes('snake')) return '🦎';
  return '🐾';
}

function buildDaysUntil(dateIso: string) {
  return differenceInCalendarDays(parseISO(dateIso), startOfDay(new Date()));
}

function toSentenceTitle(value: string | null | undefined, petName: string) {
  const trimmed = String(value || '').trim();
  return trimmed.length ? trimmed : `${petName} care`;
}

export function buildPetAttentionSurfaceItems(input: {
  pets: PetAttentionPet[];
  healthReminders: PetAttentionReminder[];
  petFoodProfiles: PetAttentionFoodProfile[];
}) {
  const pets = input.pets.filter((pet) => normalisePetName(pet.name).length > 0);
  if (!pets.length) return [] as PetAttentionSurfaceItem[];

  const petsById = new Map(pets.map((pet) => [pet.id, pet]));
  const items: PetAttentionSurfaceItem[] = [];

  const urgentPetReminder = input.healthReminders
    .filter((reminder) => reminder.subjectType === 'pet' && reminder.subjectId && reminder.dueDate && !reminder.completedAt)
    .map((reminder) => {
      const pet = petsById.get(reminder.subjectId as string);
      if (!pet || !reminder.dueDate) return null;
      const daysUntil = buildDaysUntil(reminder.dueDate);
      if (daysUntil > 14) return null;
      const petName = normalisePetName(pet.name);
      const title = toSentenceTitle(reminder.title, petName);
      return {
        id: `pet-care-${reminder.id}`,
        emoji: petEmoji(pet.petType),
        text:
          daysUntil < 0
            ? `${title} is overdue.`
            : `${title} is due in ${daysUntil} day${daysUntil === 1 ? '' : 's'}.`,
        path: '/health',
        label: 'My Pets',
        score: daysUntil < 0 ? 86 : 68,
        kind: 'pet-care' as const,
        daysUntil,
      };
    })
    .filter(Boolean)
    .sort((left, right) => {
      if (left!.score !== right!.score) return right!.score - left!.score;
      return (left!.daysUntil ?? 999) - (right!.daysUntil ?? 999);
    })[0];

  if (urgentPetReminder) {
    items.push(urgentPetReminder);
  }

  const urgentFoodSignal = input.petFoodProfiles
    .map((profile) => {
      const pet = petsById.get(profile.pet_id);
      if (!pet) return null;
      const status = buildPetFoodStatus(profile);
      if (status.tone !== 'warning' && status.tone !== 'danger') return null;
      const petName = normalisePetName(pet.name);
      const score = status.tone === 'danger' ? 84 : 66;
      const text =
        status.tone === 'danger'
          ? `${petName} food needs sorting now. ${status.headline}`
          : `${petName} food is entering the refill window. ${status.headline}`;
      return {
        id: `pet-food-${profile.id || profile.pet_id}`,
        emoji: '🥣',
        text,
        path: '/pets',
        label: 'My Pets',
        score,
        kind: 'pet-food' as const,
        daysUntil: status.daysUntilRunout,
      };
    })
    .filter(Boolean)
    .sort((left, right) => {
      if (left!.score !== right!.score) return right!.score - left!.score;
      return (left!.daysUntil ?? 999) - (right!.daysUntil ?? 999);
    })[0];

  if (urgentFoodSignal) {
    items.push(urgentFoodSignal);
  }

  if (!input.petFoodProfiles.length) {
    const firstPet = pets[0];
    items.push({
      id: `pet-food-setup-${firstPet.id}`,
      emoji: '🥣',
      text: `Add a food profile for ${normalisePetName(firstPet.name)} so Adulting can flag the next refill before it turns urgent.`,
      path: '/pets',
      label: 'My Pets',
      score: 56,
      kind: 'pet-food-setup',
      daysUntil: null,
    });
  }

  return items
    .sort((left, right) => {
      if (left.score !== right.score) return right.score - left.score;
      return (left.daysUntil ?? 999) - (right.daysUntil ?? 999);
    })
    .slice(0, 3);
}

export function buildTopPetHomeAttention(input: {
  pets: PetAttentionPet[];
  healthReminders: PetAttentionReminder[];
  petFoodProfiles: PetAttentionFoodProfile[];
}) {
  return buildPetAttentionSurfaceItems(input).find((item) => item.kind !== 'pet-food-setup' && item.score >= 65) || null;
}
